<template>
    <user-team-card
        :key="cardKey"
        :lead="props.lead"
        :user="props.lead.referrer.user"
        :team="props.lead.referrer.team"
        :assignable-users="assignableReferrerUsersCollection"
        :editable="canChangeReferrer"
        @user-change="changeReferrer($event)"
    >
        <template #title>
            Aanmaker
        </template>
    </user-team-card>
</template>

<script lang="ts" setup>
    import useClient from '@composables/useClient';
    import useLeadSecurity from '@composables/security/useLeadSecurity';
    import * as Sentry from '@sentry/vue';
    import { computed, ref } from 'vue';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';
    import { Lead, User } from '@keyvue/one-api-client';
    import { ReferralAPI, User as LegacyUser } from '@keyvue/one-api-client-legacy';
    import UserTeamCard from '@components/Cards/UserTeamCard.vue';

    interface Props {
        lead: Lead;
    }

    const props = defineProps<Props>();
    const { client, currentLegacyUser, currentLegacyCompany } = useClient();
    const { canChangeReferrer } = await useLeadSecurity(props.lead);
    const { notifySaving, notifySaved, notifyError } = useNotify();
    const eventBus = useEventBus();

    const [
        assignableReferrerUserCollection,
    ] = await Promise.all([
        await client.getSubResources(props.lead.referrer.team, User, 'available-users'),
    ]);

    const assignableReferrerUsersCollection = computed(() => assignableReferrerUserCollection.data);

    // A new key makes the card show the lead's referrer again after a change that wasn't saved
    const cardKey = ref(0);

    // keyvue-api's refusals: a 400 when the new referrer isn't an active user of the office that referred the lead,
    // a 403 (ReferralReferredByUnauthorizedException, followed by the lead id) when the user may not change it
    const REFUSED_NOT_OF_OFFICE = 'The referrer must be an active user of the office that referred the lead.';
    const REFUSED_NO_RIGHT = 'You do not have enough permissions to update';

    const changeReferrer = async (user: User) => {
        const closeNotification = notifySaving(undefined, 0);

        try {
            // Build the IRI from the id; keyvue-api hides users of offices without an accepted link.
            const legacyUser = LegacyUser.fromOne(user);
            await ReferralAPI.updateReferredBy(
                currentLegacyCompany.value,
                props.lead.id.value,
                legacyUser,
                currentLegacyUser.value,
            );
        } catch (error: any) {
            closeNotification();

            // keyvue-api's two refusals (UpdateReferralReferredByCommandHandler) are expected; anything else goes to Sentry
            const response = error?.response;
            const description = String(response?.data?.['hydra:description'] ?? '');
            const notOfOffice = response?.status === 400 && description === REFUSED_NOT_OF_OFFICE;
            const noRight = response?.status === 403 && description.startsWith(REFUSED_NO_RIGHT);
            // Unknown outcome: a server error, or no response at all (a TypeError is a fault before the request)
            const unknownOutcome = response ? response.status >= 500 : !(error instanceof TypeError);

            if (!notOfOffice && !noRight) {
                Sentry.captureException(error, {
                    tags: { endpoint: 'update_referred_by', status: response?.status ?? 'none' },
                    extra: { lead: props.lead.id.value },
                });
            }

            let message = 'De aanmaker kon niet worden gewijzigd.';
            if (notOfOffice) {
                message = 'De aanmaker moet een actieve gebruiker zijn van het kantoor dat de lead heeft aangebracht.';
            } else if (noRight) {
                message = 'Je hebt geen rechten om de aanmaker van deze lead te wijzigen.';
            } else if (unknownOutcome) {
                message = 'Het is niet zeker of de aanmaker is gewijzigd. De lead wordt opnieuw geladen.';
            }
            notifyError({ message });
            cardKey.value += 1;

            // The change may have been saved: reload the lead once keyvue-api's change can have reached one-api
            if (unknownOutcome) {
                await ReferralAPI.wait();
                eventBus.dispatch('resource-manipulated', 'Lead');
            }

            return;
        }

        notifySaved();
        closeNotification();

        eventBus.dispatch('resource-manipulated', 'Lead');
    };
</script>
